import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

function escapeCsvCell(value) {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadBlob(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * @param {string} filename - e.g. report.csv
 * @param {string[]} headers
 * @param {string[][]} rows
 * @param {string[] | null} [footer] - single footer row (same length as headers)
 */
export function exportReportCsv(filename, headers, rows, footer = null) {
  const lines = [];
  lines.push(headers.map(escapeCsvCell).join(','));
  for (const row of rows) {
    lines.push(row.map(escapeCsvCell).join(','));
  }
  if (footer && footer.length) {
    lines.push(footer.map(escapeCsvCell).join(','));
  }
  const csv = lines.join('\r\n');
  const bom = '\uFEFF';
  const name = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  downloadBlob(name, bom + csv, 'text/csv;charset=utf-8');
}

/**
 * @param {string} filename - e.g. report.xlsx
 * @param {string} sheetName - max 31 chars for Excel
 * @param {string[]} headers
 * @param {string[][]} rows
 * @param {string[] | null} [footer]
 */
export function exportReportXlsx(filename, sheetName, headers, rows, footer = null) {
  const aoa = [headers, ...rows];
  if (footer && footer.length) aoa.push(footer);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  const safeName = String(sheetName || 'Report').slice(0, 31).replace(/[:\\/?*[\]]/g, '_');
  XLSX.utils.book_append_sheet(wb, ws, safeName);
  const name = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  XLSX.writeFile(wb, name);
}

/**
 * @param {string} filename - e.g. report.pdf
 * @param {string} title - shown at top of first page
 * @param {string[]} headers
 * @param {string[][]} rows
 * @param {string[] | null} [footer]
 * @param {{ landscape?: boolean }} [options]
 */
export function exportReportPdf(filename, title, headers, rows, footer = null, options = {}) {
  const { landscape = false } = options;
  const doc = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
  const marginLeft = 14;
  let y = 12;
  if (title) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(String(title).slice(0, 120), marginLeft, y);
    y += 7;
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  autoTable(doc, {
    startY: y,
    head: [headers],
    body: rows,
    foot: footer && footer.length ? [footer] : undefined,
    showFoot: footer && footer.length ? 'lastPage' : 'never',
    styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' },
    headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
    footStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold' },
    margin: { left: marginLeft, right: 14 },
    tableWidth: 'auto',
    horizontalPageBreak: true
  });

  const name = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  doc.save(name);
}
